import { Injectable, LOCALE_ID, Inject } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { DOCUMENT } from '@angular/common';

export interface Language {
  code: string;
  name: string;
  direction: 'ltr' | 'rtl';
  flag: string;
}

@Injectable({
  providedIn: 'root',
})
export class LanguageService {
  private readonly LANGUAGE_STORAGE_KEY = 'selected-language';

  private readonly supportedLanguages: Language[] = [
    {
      code: 'en',
      name: 'English',
      direction: 'ltr',
      flag: 'assets/images/GB.svg',
    },
    {
      code: 'ar',
      name: 'العربية',
      direction: 'rtl',
      flag: 'assets/images/SA.svg',
    },
    {
      code: 'de',
      name: 'Deutsch',
      direction: 'ltr',
      flag: 'assets/images/DE.svg',
    },
  ];

  private currentLanguageSubject = new BehaviorSubject<Language>(this.supportedLanguages[0]);

  private languageChangeSubject = new Subject<Language>();

  public currentLanguage$: Observable<Language> = this.currentLanguageSubject.asObservable();
  public languageChange$: Observable<Language> = this.languageChangeSubject.asObservable();

  constructor(
    @Inject(LOCALE_ID) private localeId: string,
    @Inject(DOCUMENT) private document: Document
  ) {
    this.initializeLanguage();
  }

  private initializeLanguage(): void {
    const savedLanguage = localStorage.getItem(this.LANGUAGE_STORAGE_KEY);
    const initialLanguageCode = savedLanguage || 'en';

    const language =
      this.supportedLanguages.find(
        (lang) =>
          lang.code === initialLanguageCode || lang.code === initialLanguageCode.split('-')[0]
      ) || this.supportedLanguages[0];

    this.setLanguage(language, false);
  }

  getCurrentLanguage(): Language {
    return this.currentLanguageSubject.value;
  }

  getSupportedLanguages(): Language[] {
    return [...this.supportedLanguages];
  }

  setLanguage(language: Language, reload: boolean = true): void {
    this.currentLanguageSubject.next(language);

    this.languageChangeSubject.next(language);

    localStorage.setItem(this.LANGUAGE_STORAGE_KEY, language.code);

    this.document.documentElement.setAttribute('lang', language.code);
    this.document.documentElement.setAttribute('dir', language.direction);

    this.document.body.className = this.document.body.className.replace(/\blang-\w+\b/g, '').trim();
    this.document.body.classList.add(`lang-${language.code}`);

    if (reload) {
      this.reloadWithLocale(language.code);
    }
  }

  private reloadWithLocale(localeCode: string): void {
    console.log(`Language changed to: ${localeCode}`);
  }

  isRTL(): boolean {
    return this.getCurrentLanguage().direction === 'rtl';
  }

  isArabic(): boolean {
    return this.getCurrentLanguage().code.startsWith('ar');
  }

  isEnglish(): boolean {
    return this.getCurrentLanguage().code.startsWith('en');
  }

  getDirection(): 'ltr' | 'rtl' {
    return this.getCurrentLanguage().direction;
  }

  getLanguageClass(): string {
    const lang = this.getCurrentLanguage();
    return `lang-${lang.code} dir-${lang.direction}`;
  }

  getLanguageCode(): string {
    return this.getCurrentLanguage().code;
  }
}
